import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TagRequestOptions } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['tags']

export type TagsFilters = {
  global?: string,
  hidden?: string,
  owner_id?: number,
  owner_type?: 'Brand',
  string?: string,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: TagsFilters,
  performHttpRequests?: boolean,
  requestOptions?: TagRequestOptions,
}

function useTags(options: UseEntitiesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const {
    global: filterGlobal,
    hidden: filterHidden,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    string: filterString,
  } = filters

  const {
    updatedEntities: { tags: tagsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const tags = useSelector(reduxState => reduxState.entities.tags)

  const filteredTags = useMemo(() => {
    const filtered = Object.values(tags).filter((tag) => {
      const {
        global, hidden, id, owner_id, owner_type, title,
      } = tag

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchGlobal = filterGlobal ? global.toString() === filterGlobal : true
      const matchHidden = filterHidden ? hidden.toString() === filterHidden : true
      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType && filterOwnerId ? owner_type === filterOwnerType : true

      return matchGlobal && matchHidden && matchOwnerId && matchOwnerType && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [tagsUpdatedAt, JSON.stringify(filters)])

  const filteredTagsCount = filteredTags.length
  const hasFilteredTags = !!filteredTagsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredTagsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingTags } = useReduxAction(
    'tags',
    'loadTags',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTags,
    filteredTagsCount,
    hasFilteredTags,
    loading: loadingTags,
  }
}

export default useTags
